"use client";

import { isRedirectDisabled } from "@/apiUtils/commonUtils";
import { analyticKeyIds, sessionFlowConstants, sessionQueryParams } from "@/apiUtils/constants";
import UserIcon from "@/assets/icons/user.svg?react";
import useCRouter from "@/hooks/useCRouter";
import useSearchParams from "@/hooks/useSearchParams";
import { AvailableSocialLogins, useGlobalStore } from "@/store/store";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
// import SignInSignUpModal from "@/components/MagrabiComponents/CheckAuth";
import { AvatarPlaceholder } from "./avatarPlaceholder/AvatarPlaceholder";
import dynamic from "next/dynamic";
import { setLocalStorage } from "@/apiUtils/localStorageUtils";
import { getFieldValue, getLangFromLocale } from "@/utils/common";
import { useParams } from "next/navigation";

const SignInSignUpModal = dynamic(
  () => import("@/components/MagrabiComponents/SignInSignUpModal"),
  { ssr: false },
);
const CheckAuth = dynamic(() => import("@/components/MagrabiComponents/CheckAuth"), { ssr: false });

interface Props {
  usePrimaryColor: boolean;
  primaryTextColor?: string;
  token?: string;
  enableAppleLogin: boolean;
  enableGoogleLogin: boolean;
  enableFacebookLogin: boolean;
  onClick?: () => void;
}

const ProfileIcon = ({
  usePrimaryColor,
  primaryTextColor = "white",
  enableAppleLogin,
  enableFacebookLogin,
  enableGoogleLogin,
  onClick,
}: Props) => {
  const { push } = useCRouter();
  const { locale } = useParams();
  const lang = getLangFromLocale(locale as string);

  const isAuthenticated = useGlobalStore((state) => state.isAuthenticated);
  const user = useGlobalStore((state) => state.user);

  const socialProviders = useMemo(() => {
    // console.log("They have changed", {enableAppleLogin, enableFacebookLogin, enableGoogleLogin});

    const socialProviders: AvailableSocialLogins[] = [];
    if (enableAppleLogin) socialProviders.push("apple.com");
    if (enableFacebookLogin) socialProviders.push("facebook.com");
    if (enableGoogleLogin) socialProviders.push("google.com");

    return socialProviders;
  }, [enableAppleLogin, enableFacebookLogin, enableGoogleLogin]);

  const setAvailableSocialLogins = useGlobalStore((state) => state.setAvailableSocialLogins);

  useEffect(() => {
    setAvailableSocialLogins(socialProviders);
  }, []);

  const [isClient, setIsClient] = useState<boolean>(false);
  const [loadedOnce, setLoadedOnce] = useState<boolean>(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const [isLoginModalLoaded, setIsLoginModalLoaded] = useState<boolean | undefined>(false);

  const { updateSearchParam, searchParams } = useSearchParams();

  const { sessionInfoM, sessionInfoTabs, isCheckout } = useMemo(() => {
    const allTabs = [
      sessionFlowConstants.LOGIN_EMAIL,
      sessionFlowConstants.LOGIN_PHONE,
      sessionFlowConstants.REGISTER,
      sessionFlowConstants.GUEST,
    ];
    const utmSource = searchParams?.get("utm_source");

    // For cart abandon flow, only show login tab (no register, no guest)
    const isCartAbandon = utmSource === sessionFlowConstants.ABANDON_EMAIL;
    const finalTabs = isCartAbandon
      ? [sessionFlowConstants.LOGIN_EMAIL, sessionFlowConstants.LOGIN_PHONE]
      : allTabs;

    return {
      sessionInfoM: searchParams.get(sessionQueryParams.LOGIN_MODAL_STATUS),
      sessionInfoTabs: finalTabs,
      isCheckout: searchParams.get(sessionQueryParams.IS_CHECKOUT),
    };
  }, [searchParams]);

  // Auto-open login modal when cart abandon flow is detected
  useEffect(() => {
    if (!sessionInfoM) {
      const utmSource = searchParams?.get("utm_source");
      if (utmSource === sessionFlowConstants.ABANDON_EMAIL && !isAuthenticated) {
        updateSearchParam([
          { key: sessionQueryParams.LOGIN_MODAL_STATUS, value: sessionFlowConstants.LOGIN_EMAIL },
        ]);
      }
    }
  }, [searchParams, sessionInfoM, isAuthenticated, updateSearchParam]);

  // const memoizedProviders = useMemo(() => socialProviders, [socialProviders])

  const [activeTab, setActiveTab] = useState<string>(sessionInfoM || sessionInfoTabs[0]);

  const show = useMemo(() => {
    if (sessionInfoM) {
      setActiveTab(sessionInfoM);
      return sessionInfoTabs.includes(sessionInfoM);
    }
    return false;
  }, [sessionInfoM, sessionInfoTabs]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setLoadedOnce(true);
    }, 2000);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (activeTab && !user?.email) {
      setLocalStorage(analyticKeyIds.analyticUserJourney, activeTab);
    }
  }, [activeTab, user]);

  const handleProfileIconClick = (tab?: string) => {
    if (isAuthenticated) {
      push("/my-account/dashboard");
      onClick?.();
      return;
    }
    // Logic to handle profile icon click
    setIsLoginModalLoaded(true);
    updateSearchParam([
      { key: sessionQueryParams.LOGIN_MODAL_STATUS, value: activeTab || tab || sessionInfoTabs[0] },
      { key: sessionQueryParams.DISABLE_REDIRECT, value: "true" },
    ]);

    onClick?.();
  };

  const handleOnLoaded = () => {
    // Logic to handle when modal is loaded
    setIsLoginModalLoaded(false);
  };

  const handleClose = () => {
    // Logic to handle modal close
    setActiveTab(sessionInfoM || sessionInfoTabs[0]);
    setIsLoginModalLoaded(false);
    updateSearchParam([
      { key: sessionQueryParams.LOGIN_MODAL_STATUS, value: undefined },
      {
        key: sessionQueryParams.IS_CHECKOUT,
        value: undefined,
      },
      { key: sessionQueryParams.DISABLE_REDIRECT, value: undefined },
      { key: "utm_source", value: undefined },
      { key: "source", value: undefined },
    ]);
  };

  // const memoizedProviders = useMemo(() => socialProviders,[socialProviders])

  // console.log("socialProviders parent parent", socialProviders);

  return (
    <>
      <div
        onClick={(e) => {
          handleProfileIconClick(sessionInfoTabs[0]);
        }}
        style={isLoginModalLoaded ? { cursor: "wait" } : { cursor: "pointer" }}
      >
        {isAuthenticated === false ? (
          <UserIcon
            height={24}
            width={24}
            //@ts-ignore
            title={getFieldValue(lang, "Profile", "حساب تعريفي")}
          />
        ) : (
          <AvatarPlaceholder
            primaryTextColor={primaryTextColor}
            secodaryTextColor="black"
            usePrimaryTextColor={usePrimaryColor}
            size="extra-small"
            color={primaryTextColor}
            placeholderText={
              user && user.name && user.family_name ? `${user.name[0]}${user.family_name[0]}` : "SB"
            }
          />
        )}
      </div>
      {typeof loadedOnce ? <CheckAuth /> : ""}
      {typeof loadedOnce && !isAuthenticated && isClient
        ? createPortal(
            <SignInSignUpModal
              show={!!show}
              tabs={sessionInfoTabs}
              activeTab={activeTab}
              onClose={handleClose}
              isCheckoutFlow={!!isCheckout}
              onLoaded={handleOnLoaded}
              translations={{}}
              disableRedirect={isRedirectDisabled(searchParams)}
            />,
            document.body,
            "key-session",
          )
        : null}
    </>
  );
};

export default ProfileIcon;
